<template>
  <Dialog :visible="showDialog" modal
    :style="{ width: '75rem' }" class="mx-4 h-5/6" @update:visible="closePopup">
    <template #header>
      <div class="inline-flex items-center justify-center gap-2">
        <span class="font-bold whitespace-nowrap text-xl">
          {{ $t('TaskCreatorOverlayComponent.createEvent') }}
        </span>
      </div>
    </template>
    <div class="relative flex flex-col">
      <div class="flex relative justify-start flex-row items-center mx-8 gap-4">
        <p class="text-xl">{{ $t('TaskCreatorOverlayComponent.chooseEvent') }}</p>
        <Button :label="selectedTabLabel" @click="toggle" icon="pi pi-check" iconPos="right"
        class="min-w-48 choose-button" id="btn_selectTab"/>
        <Popover ref="op">
          <div class="flex flex-col gap-4">
            <ul class="list-none p-0 m-0 flex flex-col">
              <li v-for="tab in visibleTabs" :key="tab.catalogKey"
                class="flex items-center gap-2 px-2 py-3
                hover:bg-emphasis cursor-pointer rounded-border">
                <Button v-tooltip="getTooltip(tab.catalogKey)" :disabled="getDisabledTab(tab.catalogKey)"
                @click.prevent="setSelected(tab.catalogKey)" class="options-button" id="btn_tab">
                  {{ tabLabel(tab.catalogKey) }}
                </Button>
              </li>
            </ul>
         </div>
       </Popover>
      </div>
      <div class="w-auto">
        <kss-creator-component
          v-if="selectedTab === 'kssTracker'"
          :data="data"
          @close-overlay="closePopup"/>
        <mindset360-creator-component
          v-if="selectedTab === 'externalEvaluation'"
          :data="data"
          @close-overlay="closePopup"/>
        <personal-goals-form-component
          v-if="selectedTab === 'personalGoals'"
          :data="data"
          @close-overlay="closePopup"/>
        <workshop-form-component
          v-if="selectedTab === 'workshops'"
          @close-overlay="closePopup"
          :data="data"/>
      </div>
    </div>
  </Dialog>
</template>

<script>
import KssCreatorComponent from '@/components/Home/KssCreator/KssCreatorComponent.vue';
import Mindset360CreatorComponent
  from '@/components/Home/Mindset360Creator/Mindset360CreatorComponent.vue';
import PersonalGoalsFormComponent from '@/components/Home/PersonalGoalsComponent/PersonalGoalsFormComponent.vue';
import WorkshopFormComponent from '@/components/Home/WorkshopComponent/WorkshopFormComponent.vue';
import {
  DEFAULT_TASK_CREATOR_TAB,
  TASK_CREATOR_TABS,
} from '@/constants/taskCreatorTabs';
import {
  getFeatureDisplayNameOrFallback,
  isFeatureEnabledForCustomer,
} from '@/utils/featureDisplay';
import { Button, Dialog, Popover } from 'primevue';

export default {
  name: 'TaskCreatorOverlayComponent',
  components: {
    Mindset360CreatorComponent,
    KssCreatorComponent,
    PersonalGoalsFormComponent,
    WorkshopFormComponent,
    Dialog,
    Popover,
    Button,
  },
  props: {
    data: Object,
  },
  data() {
    return {
      selectedTab: DEFAULT_TASK_CREATOR_TAB,
      showDialog: true,
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'externalEvaluation',
        this.$t('FallbackTexts.externalEvaluation'),
        this.$i18n?.locale,
      );
    },
    featureNamePersonalGoals() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'personalGoals',
        this.$t('FallbackTexts.personalGoals'),
        this.$i18n?.locale,
      );
    },
    featureNameKssTracker() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'kssTracker',
        this.$t('FallbackTexts.collaborationTracker'),
        this.$i18n?.locale,
      );
    },
    getExternalEvaluationSchemas() {
      return this.$store.getters['assessmentSchemas/getExternalEvaluationSchemas'] ?? [];
    },
    getLastExternalEvaluationDates() {
      return this.$store.getters['assessmentSchemas/getLastExternalEvaluationDates'] ?? {};
    },
    taskCreatorTabs() {
      return TASK_CREATOR_TABS;
    },
    getExternalEvaluationEnabled() {
      return this.$store.getters.getUser?.externalEvaluationEnabled;
    },
    getAvailableExternalEvaluationSchemas() {
      const lastEvalDates = this.getLastExternalEvaluationDates;
      const currentDate = new Date();
      const sixMonthsAgo = new Date(currentDate);
      sixMonthsAgo.setMonth(currentDate.getMonth() - 6);

      return this.getExternalEvaluationSchemas.filter((schema) => {
        const lastEvalDate = lastEvalDates[schema.type];
        if (!lastEvalDate) return true;
        const evalDate = new Date(lastEvalDate);
        return evalDate <= sixMonthsAgo;
      });
    },
    areAllExternalEvaluationsBlocked() {
      if (!this.getExternalEvaluationSchemas || this.getExternalEvaluationSchemas.length === 0) {
        return true;
      }
      return this.getAvailableExternalEvaluationSchemas.length === 0;
    },
    visibleTabs() {
      return this.taskCreatorTabs.filter((tab) => this.isTabVisible(tab.catalogKey));
    },
    selectedTabLabel() {
      return this.tabLabel(this.selectedTab);
    },
  },
  created() {
    this.selectedTab = this.resolveInitialTab();
    this.$store.dispatch('assessmentSchemas/fetchAssessments');
    this.$store.dispatch('assessmentSchemas/fetchLastExternalEvaluationDates');
  },
  methods: {
    tabLabel(catalogKey) {
      const tab = this.taskCreatorTabs.find((t) => t.catalogKey === catalogKey);
      const fallback = tab ? this.$t(tab.fallbackKey) : catalogKey;
      return getFeatureDisplayNameOrFallback(
        this.$store,
        catalogKey,
        fallback,
        this.$i18n?.locale,
      );
    },
    isTabVisible(catalogKey) {
      if (!isFeatureEnabledForCustomer(this.$store, catalogKey)) {
        return false;
      }
      if (catalogKey === 'externalEvaluation' && !this.getExternalEvaluationEnabled) {
        return false;
      }
      return true;
    },
    async closePopup(val) {
      if (!val) {
        this.$store.commit('removeFrontendOverlay', this.data.id);
        this.showDialog = false;
      }
    },
    setSelected(catalogKey) {
      if (this.getDisabledTab(catalogKey)) {
        return;
      }
      this.selectedTab = catalogKey;
      this.$refs.op.hide();
    },
    getDisabledTab(catalogKey) {
      if (catalogKey === 'externalEvaluation') {
        return this.areAllExternalEvaluationsBlocked;
      }
      if (catalogKey === 'personalGoals') {
        return this.$store.getters.getActiveGoalsCount
         >= this.$store.getters.getPersonalGoalsMaxLimit;
      }
      return false;
    },
    resolveInitialTab() {
      const storeTab = this.$store.getters.getSelectedCreatorTab
        ?? this.$store.state?.content?.selectedCreatorTab
        ?? this.$store.state?.selectedCreatorTab
        ?? '';
      const normalized = this.normalizeTabSelection(storeTab);
      if (normalized && this.isTabVisible(normalized)) {
        this.$store.commit('setSelectedCreateTab', '');
        return normalized;
      }
      const firstVisible = this.visibleTabs[0]?.catalogKey ?? DEFAULT_TASK_CREATOR_TAB;
      this.$store.commit('setSelectedCreateTab', firstVisible);
      return firstVisible;
    },
    normalizeTabSelection(value) {
      if (!value || typeof value !== 'string') return null;
      if (this.taskCreatorTabs.some((t) => t.catalogKey === value)) {
        return value;
      }
      const byLabel = this.taskCreatorTabs.find(
        (t) => this.tabLabel(t.catalogKey) === value,
      );
      return byLabel?.catalogKey ?? null;
    },
    toggle(event) {
      this.$refs.op.toggle(event);
    },
    getTooltip(catalogKey) {
      if (this.getDisabledTab(catalogKey)) {
        if (catalogKey === 'externalEvaluation') {
          return this.$t('TaskCreatorOverlayComponent.alreadyRequested', { text: this.featureName });
        }
        if (catalogKey === 'personalGoals') {
          return this.$t('TaskCreatorOverlayComponent.personalGoalsError', { text: this.featureNamePersonalGoals });
        }
      }
      return '';
    },
  },
};
</script>

<style scoped>
.choose-button {
  @apply text-black bg-white border border-[var(--color-orange)];
}

.choose-button:not(:disabled):hover {
  @apply bg-[var(--color-orange)] border border-[var(--color-orange)];
}

.options-button, .options-button:not(:disabled):hover {
  @apply bg-white text-black border-none;
}

.options-button:not(:disabled):hover {
  @apply font-bold;
}
</style>
